import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TagsLanguageService } from '../../features/tags-language/tagsLanguage.service';
import { ToastrService } from 'ngx-toastr';
import { ColoringImageService } from '../../features/coloring-image/coloring-image.service';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import { noOnlySpacesValidator, noOnlySpecialCharsAlphaValidator } from '../../core/helpers/validator';
import { Storage, ref, uploadBytes, getDownloadURL } from '@angular/fire/storage';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FullscreenLoaderComponent } from '../../shared/components/loader-component/loader.component';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { ContentLoaderComponent } from '../../shared/components/content-loader-component/contentLoader.component';
import { FilenameGeneratorUtil } from '../../core/utils/filename-generator';

@Component({
  selector: 'app-coloring-image-form-component',
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatSelectModule,
    FullscreenLoaderComponent,
    ContentLoaderComponent,
  ],
  templateUrl: './coloring-image-form-component.component.html',
  styleUrl: './coloring-image-form-component.component.scss'
})
export class ColoringImageFormComponentComponent {
  @ViewChild('imagefileInput') imagefileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('thumbFileInput') thumbFileInput!: ElementRef<HTMLInputElement>;
  readonly storage = inject(Storage);
  readonly dialog = inject(MatDialog);
  public languages: any[] = [];
  public coloringImageTranslations: {
    id?: number;
    langId: number;
    title: string;
  }[] = [];
  public TAGS: any[] = [];
  public selectedTags: any[] = [];
  public langCode: string = '';
  imageUrl: string | null = null;
  imageUploading = false;
  loading = false;
  isCustomColoringImage = false;
  isEditingTranslation: boolean = false;
  isSubmitting = false;
  contentLoading: boolean = false;
  coloringImageId?: number;
  imageFileName?: string | null;
  existingColoringImageData: any = null;
  public showTagError: boolean = false;
  thumbImageUrl: string | null = null;
  thumbUploading = false;
  thumbFileName?: string | null;
  currTranslationId?: number;
  editingTranslation?: any;
  public mainForm!: FormGroup;
  editingLangId: any;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private tagsService: TagsLanguageService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private coloringImageService: ColoringImageService
  ) {
  }

  noOnlySpecialCharsAlphaValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value?.trim() || '';

      if (!value) {
        return { required: true };
      }

      if (/^[0-9]+$/.test(value)) {
        return { onlyNumbers: true };
      }

      const hasLetterOrNumber = /[\p{L}\p{N}]/u.test(value);

      if (hasLetterOrNumber) {
        return null;
      }

      return { onlySpecialChars: true };
    };
  }

  ngOnInit() {

    this.mainForm = this.fb.group({
      imageUrl: [''],
      thumbURL: [''],
      title: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(50),
          this.noOnlySpecialCharsAlphaValidator(),
          noOnlySpacesValidator(),
        ],
      ],
      langId: [null, Validators.required],
    }),

      this.route.paramMap.subscribe((params) => {
        const id = params.get('id');
        this.coloringImageId = id ? +id : undefined;

        if (this.coloringImageId) {
          // EDIT MODE
          this.contentLoading = true;
          this.getColoringImageById(this.coloringImageId);
        } else {
          // CREATE MODE
          this.coloringImageTranslations = [];
          this.mainForm.get('langId')?.setValue(1);
        }
      });

    this.getLanguages();
    this.getAllTagsInitially(this.langCode);
  }


  onTitleInput(): void {
    const titleControl = this.mainForm.get('title');
    if (titleControl?.valid) {
      titleControl.setErrors(null);
    }
  }

  onLanguageSelect(event: MatSelectChange): void {
    const selectedId = event.value;
    const selectedLang = this.languages.find((lang) => lang.id === selectedId);
    if (selectedLang) {
      this.langCode = selectedLang.code;
      this.getAllTagsInitially(this.langCode);
    }
    this.applyLanguage(event.value);
  }

  getAllTagsInitially(lang: any): void {
    this.tagsService.getAllTagsWithFilter(lang).subscribe({
      next: (res) => {
        if (res?.data) {
          this.TAGS = res.data
            .filter((tag: any) => tag.type === 'coloring')
            .map((tag: any) => {
              const translation = tag.tagTranslation?.[0];
              return translation ? { ...tag, name: translation.tagName } : null;
            })
            .filter((tag: any) => tag !== null);

          if (this.existingColoringImageData?.tags?.length) {
            this.selectedTags = this.TAGS.filter(tag =>
              this.existingColoringImageData.tags.includes(tag.id)
            );
          }
        }
      },
      error: (err) => console.log(err),
    });
  }

  isTagSelected(tag: any): boolean {
    return this.selectedTags?.some((t: any) => t.id === tag.id);
  }

  toggleTag(tag: any): void {
    const index = this.selectedTags.findIndex((t: any) => t.id === tag.id);

    if (index === -1) {
      this.selectedTags = [...this.selectedTags, tag];
    } else {
      this.selectedTags = this.selectedTags.filter((t: any) => t.id !== tag.id);
    }
  }

  private applyLanguage(langId: number): void {
    const selectedLang = this.languages.find(lang => lang.id === langId);
    if (selectedLang) {
      this.langCode = selectedLang.code;
      this.getAllTagsInitially(this.langCode);
    }
  }

  getLanguages(): void {
    this.tagsService.getLangauges().subscribe({
      next: (res) => {
        if (res?.data) {
          this.languages = res?.data;
          this.updateFormControlsState();
        }
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  editTranslation(row: any) {
    this.isEditingTranslation = true;
    this.currTranslationId = row.id;
    this.editingLangId = row.langId;
    this.mainForm.patchValue({
      langId: row.langId,
      title: row.title
    });
  }

  isLangUsed(langId: number): boolean {
    if (this.isEditingTranslation && this.editingLangId === langId) {
      return false;
    }

    return this.coloringImageTranslations.some(
      t => t.langId === langId
    );
  }

  getColoringImageById(id: any) {
    this.coloringImageService.getColoringImgById(id).subscribe({
      next: (res) => {
        const coloringImageData = res;
        if (coloringImageData) {
          this.existingColoringImageData = coloringImageData;

          this.imageUrl = coloringImageData.imageUrl;
          this.thumbImageUrl = coloringImageData.thumbURL;

          this.imageFileName = coloringImageData.imageUrl
            ? FilenameGeneratorUtil.generateRandomFilename(coloringImageData.imageUrl)
            : null;
          this.thumbFileName = coloringImageData.thumbURL
            ? FilenameGeneratorUtil.generateRandomFilename(coloringImageData.thumbURL)
            : null;

          this.coloringImageTranslations = coloringImageData.translations.map((t: any) => ({
            id: t.id,
            langId: t.langId,
            title: t.title,
          }));

          if (this.TAGS.length > 0) {
            this.selectExistingTags();
          } else {
            const checkTags = setInterval(() => {
              if (this.TAGS.length > 0) {
                this.selectExistingTags();
                clearInterval(checkTags);
              }
            }, 200);
          }
        }
        this.contentLoading = false;
      },
      error: (err) => {
        this.contentLoading = false;
      },
    });
  }

  private selectExistingTags() {
    this.selectedTags = this.TAGS.filter(tag =>
      this.existingColoringImageData.tags.includes(tag.id)
    );
  }

  onCancel() {
    this.router.navigate(['/coloring-images']);
  }

  getLanguageName(langId: number): string {
    const lang = this.languages.find((l) => l.id === langId);
    return lang ? lang.name : `Lang ID: ${langId}`;
  }

  get sortedColoringImageTranslations() {
    return this.coloringImageTranslations
      ?.slice()
      .sort((a, b) => (a.langId === 1 ? -1 : b.langId === 1 ? 1 : 0));
  }

  addColoringImageTable(): void {
    this.mainForm.markAllAsTouched();

    if (this.mainForm.invalid) {
      this.displayValidationErrors();
      return;
    }

    const { title, langId } = this.mainForm.value;

    if (!langId) {
      return;
    }

    if (!this.isEditingTranslation) {
      const alreadyExists = this.coloringImageTranslations.some(t => t.langId === langId);
      if (alreadyExists) {
        const langName = this.getLanguageName(langId);
        this.toastr.warning(`${langName} translation already added`);
        return;
      }
    }

    if (this.isEditingTranslation && this.currTranslationId) {
      this.coloringImageTranslations.push({
        id: this.currTranslationId,
        title: title.trim(),
        langId,
      });
    } else {
      this.coloringImageTranslations.push({
        id: undefined,
        title: title.trim(),
        langId,
      });
    }

    this.updateFormControlsState();

    // Reset editing state
    this.isEditingTranslation = false;
    this.currTranslationId = undefined;

    // Clear form for next entry
    this.mainForm.patchValue({
      title: '',
      langId: null
    });
    this.mainForm.controls['title'].markAsUntouched();
    this.mainForm.controls['langId'].markAsUntouched();
  }

  private displayValidationErrors(): void {
    const titleControl = this.mainForm.get('title');
    const langControl = this.mainForm.get('langId');

    if (langControl?.errors?.['required']) {
      this.toastr.warning('Please select a language');
    }
  }

  removeColoringImageTranslation(index: number, id?: number): void {
    const dialogRef = this.dialog.open(ConfirmDialog);
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        if (id) {
          this.coloringImageService.deleteColoringImgTranslation(id).subscribe({
            next: () => {
              this.coloringImageTranslations.splice(index, 1);
            },
          });
        } else {
          this.coloringImageTranslations.splice(index, 1);
        }
      }
    });
  }

  editColoringImageTranslation(index: number, id?: number): void {
    const translation = this.coloringImageTranslations[index];
    if (!translation) return;
    const currentTitle = this.mainForm.get('title')?.value;
    const currentLangId = this.mainForm.get('langId')?.value;
    if (currentTitle && currentLangId && this.mainForm.valid) {
      this.addColoringImageTable();
    }

    // Remove the translation from the array first
    this.coloringImageTranslations.splice(index, 1);

    this.currTranslationId = id;
    this.isEditingTranslation = true;
    this.editingLangId = translation.langId;

    // Enable form controls for editing
    this.mainForm.get('title')?.enable();
    this.mainForm.get('langId')?.enable();

    this.mainForm.patchValue({
      title: translation.title,
      langId: translation.langId,
    });
    this.applyLanguage(translation.langId);
    this.mainForm.markAsPristine();
    this.mainForm.markAsUntouched();
    this.mainForm.updateValueAndValidity();
    this.scrollUp('translation');
  }

  cancelEdit(): void {
    this.isEditingTranslation = false;
    this.currTranslationId = undefined;

    // Reset form
    this.mainForm.patchValue({
      title: '',
      langId: null
    });
    this.mainForm.controls?.['title'].setErrors(null);
    this.mainForm.controls?.['title'].markAsUntouched();
    this.mainForm.controls?.['langId'].setErrors(null);
    this.mainForm.controls?.['langId'].markAsUntouched();

    // Update form controls state based on current translations
    this.updateFormControlsState();
  }


  scrollUp(elementId: string): void {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }

  async onImageUpload(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    const allowedType = 'image/svg+xml';
    const maxSize = 5 * 1024 * 1024;

    const isSvgByName = file.name?.toLowerCase().endsWith('.svg');

    if (file.type !== allowedType || !isSvgByName) {
      this.toastr.error('Please upload an SVG file only.');
      input.value = '';
      return;
    }

    if (file.size > maxSize) {
      this.toastr.error('Choose a smaller SVG file (less than 5 MB).');
      input.value = '';
      return;
    }

    this.imageUploading = true;

    // Use a unique path to avoid collisions
    const filePath = `coloring-images/${file.name.replace(/\s+/g, '_')}_${Date.now()}.svg`;
    const fileRef = ref(this.storage, filePath);

    try {
      // Upload will retain content-type from the file object
      await uploadBytes(fileRef, file);
      this.imageUrl = await getDownloadURL(fileRef);
      this.imageFileName = file.name;
    } catch (err) {
      console.error(err);
      this.toastr.error('Upload failed. Please try again later.');
    } finally {
      this.imageUploading = false;
    }
  }

  clearImageUpload(): void {
    this.imageFileName = null;
    this.imageUrl = null;
    if (this.imagefileInput) {
      this.imagefileInput.nativeElement.value = '';
    }
    this.toastr.info('Image removed');
  }

  private updateFormControlsState(): void {
    if (this.coloringImageTranslations.length === this.languages.length) {
      this.mainForm.get('title')?.disable();
      this.mainForm.get('langId')?.disable();
    } else {
      this.mainForm.get('title')?.enable();
      this.mainForm.get('langId')?.enable();
    }
  }

  createColoringImage() {
    if (this.isEditingTranslation) {
      this.toastr.warning('Please save or cancel the current translation edit');
      return;
    }

    if (!this.imageUrl) {
      this.toastr.warning('Please upload an image');
      return;
    }

    const englishTranslation = this.coloringImageTranslations.find(
      (t) => t.langId === 1
    );

    if (!englishTranslation) {
      this.toastr.error('Coloring Image in English is mandatory');
      return;
    }

    if (this.selectedTags.length === 0) {
      this.showTagError = true;
      this.toastr.warning('Please select at least one tag');
      this.scrollToTagsSection();
      return;
    }

    this.showTagError = false;

    const payload = {
      imageUrl: this.imageUrl,
      thumbURL: this.thumbImageUrl,
      translations: this.coloringImageTranslations,
      tags: this.selectedTags.map(tag => tag.id),
    };

    this.loading = true;
    this.isSubmitting = true;

    if (this.coloringImageId) {
      this.coloringImageService
        .updateColoringImage(this.coloringImageId, payload)
        .subscribe({
          next: (res) => {
            this.toastr.success('Coloring Image updated successfully.');
            this.router.navigate(['/coloring-images']);
          },
          error: (err) => {
            this.isSubmitting = false;
            this.loading = false;
          },
        });
    } else {
      this.coloringImageService.createColoringImage(payload).subscribe({
        next: (res) => {
          this.toastr.success('Coloring Image created successfully.');
          this.router.navigate(['/coloring-images']);
        },
        error: (err) => {
          this.isSubmitting = false;
          this.loading = false;
        },
      });
    }
  }

  scrollToTagsSection(): void {
    const tagsSection = document.querySelector('.tags-image-section');
    if (tagsSection) {
      tagsSection.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }

  async onThumbImageUpload(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 15 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      this.toastr.error('Choose proper Image format');
      input.value = '';
      return;
    }

    if (file.size > maxSize) {
      this.toastr.error('Choose a smaller image file (less than 15 MB)');
      input.value = '';
      return;
    }

    this.thumbUploading = true;
    if (!navigator.onLine) {
      this.toastr.warning(
        'No internet connection. Please check your connection and try again.'
      );
      input.value = '';
      this.thumbUploading = false;
      return;
    }

    const filePath = `story-thumb/${file.name}_${Date.now()}`;
    const fileRef = ref(this.storage, filePath);

    try {
      await uploadBytes(fileRef, file);
      if (!navigator.onLine) {
        input.value = '';
        this.toastr.warning('Upload failed. No internet connection');
        this.thumbUploading = false;
        return;
      }
      this.thumbImageUrl = await getDownloadURL(fileRef);
      this.thumbFileName = file.name;
    } catch (err) {
      this.toastr.error(
        'Upload failed due to an internal error. Please try again later.'
      );
    } finally {
      this.thumbUploading = false;
    }
  }

  clearThumbUpload(): void {
    this.thumbFileName = null;
    this.thumbImageUrl = null;
    if (this.thumbFileInput) {
      this.thumbFileInput.nativeElement.value = '';
    }
  }
}
